import React from 'react';
import './FmComposition.css';

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

type FmElement =
  | 'div'
  | 'span'
  | 'main'
  | 'header'
  | 'footer'
  | 'section'
  | 'ul'
  | 'ol'
  | 'li'
  | 'fieldset'
  | 'button'
  | 'output'
  | 'p'
  | 'small'
  | 'strong'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'button'
  | 'output';

// 'section' is the semantic section-rhythm band: it resolves to the global
// --fm-section-gap token, so every stack that separates major report/story
// sections is tuned from one place. Prefer it over a fixed gap for
// section-to-section spacing.
type FmGap = 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'section';
type FmInset = 'none' | 'xs' | 'sm' | 'md' | 'lg';

interface FmBaseProps extends React.HTMLAttributes<HTMLElement> {
  as?: FmElement;
  type?: React.ButtonHTMLAttributes<HTMLButtonElement>['type'];
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
  children: React.ReactNode;
  'data-testid'?: string;
}

const renderElement = (
  as: FmElement,
  props: React.HTMLAttributes<HTMLElement>,
  children: React.ReactNode,
): React.ReactElement => React.createElement(as, props, children);

export interface FmRoutePageProps extends FmBaseProps {
  width?: 'page' | 'flush';
  /**
   * Route background ownership. Default route pages paint the app background;
   * underlay routes can opt out so full-bleed section bands/map media are the
   * only visible surfaces behind glass chrome.
   */
  surface?: 'default' | 'transparent';
  /** `shell` fills the app shell outlet without adding viewport min-height. */
  fit?: 'viewport' | 'shell' | 'content';
  layout?: 'block' | 'column';
  scroll?: 'none' | 'auto' | 'hidden' | 'visible';
  position?: 'normal' | 'relative';
  grow?: 'normal' | 'static' | 'fill' | 'content';
  padY?: 'none' | 'sm' | 'md' | 'lg';
}

interface RoutePageClassNameParams {
  width: 'page' | 'flush';
  surface: 'default' | 'transparent';
  fit: 'viewport' | 'shell' | 'content';
  layout: 'block' | 'column';
  scroll: 'none' | 'auto' | 'hidden' | 'visible';
  position: 'normal' | 'relative';
  grow: 'normal' | 'static' | 'fill' | 'content';
  padY: 'none' | 'sm' | 'md' | 'lg';
  className: string | undefined;
}

const getRoutePageClassNames = ({
  width,
  surface,
  fit,
  layout,
  scroll,
  position,
  grow,
  padY,
  className,
}: RoutePageClassNameParams): string =>
  cx(
    'fm-route-page',
    width === 'flush' && 'fm-route-page--flush',
    surface === 'transparent' && 'fm-route-page--transparent',
    fit !== 'viewport' && `fm-route-page--fit-${fit}`,
    layout === 'column' && 'fm-route-page--column',
    scroll !== 'none' && `fm-route-page--scroll-${scroll}`,
    position === 'relative' && 'fm-route-page--relative',
    grow !== 'normal' && `fm-route-page--grow-${grow}`,
    `fm-route-page--pad-y-${padY}`,
    className,
  );

export const FmRoutePage: React.FC<FmRoutePageProps> = ({
  as = 'main',
  width = 'page',
  surface = 'default',
  fit = 'viewport',
  layout = 'block',
  scroll = 'none',
  position = 'normal',
  grow = 'normal',
  padY = 'none',
  className,
  children,
  ...rest
}) =>
  renderElement(
    as,
    {
      ...rest,
      className: getRoutePageClassNames({
        width,
        surface,
        fit,
        layout,
        scroll,
        position,
        grow,
        padY,
        className,
      }),
    },
    children,
  );

export interface FmStackProps extends FmBaseProps {
  gap?: FmGap;
  /** Cross-axis alignment of stacked items. Defaults to stretch. */
  align?: 'stretch' | 'start' | 'center' | 'end';
  /** Flex growth when the stack is placed inside an inline/flex primitive. */
  grow?: 'none' | 'fill' | 'content';
  /**
   * Refuse to collapse below the stack's intrinsic content minimum
   * (min-width: min-content) instead of the default shrink-to-zero. Use inside
   * table cells that carry an unbreakable chip/badge so a greedy neighbour can
   * never crush the cell and let the chip overprint the next column
   * Opt-in only — the default keeps the responsive
   * shrink-to-zero behaviour for wide children (tables, maps).
   */
  fit?: boolean;
}

export const FmStack: React.FC<FmStackProps> = ({
  as = 'div',
  gap = 'md',
  align = 'stretch',
  grow = 'none',
  fit = false,
  className,
  children,
  ...rest
}) =>
  renderElement(
    as,
    {
      ...rest,
      className: cx(
        'fm-stack',
        `fm-stack--gap-${gap}`,
        align !== 'stretch' && `fm-stack--align-${align}`,
        grow !== 'none' && `fm-stack--grow-${grow}`,
        fit && 'fm-stack--fit',
        className,
      ),
    },
    children,
  );

export interface FmInlineProps extends FmBaseProps {
  /** Button type when the inline primitive renders as a button. */
  type?: 'button' | 'submit' | 'reset';
  gap?: FmGap;
  align?: 'start' | 'center' | 'end' | 'baseline';
  justify?: 'start' | 'end' | 'between';
  wrap?: boolean;
  /** Render as a block flex row that spans its container. */
  block?: boolean;
  direction?: 'row' | 'column';
  /** Flex growth when the inline row is nested inside another flex primitive. */
  grow?: 'none' | 'fill' | 'content';
}

export const FmInline: React.FC<FmInlineProps> = ({
  as = 'div',
  gap = 'sm',
  align = 'center',
  justify = 'start',
  wrap = true,
  block = false,
  direction = 'row',
  grow = 'none',
  className,
  children,
  ...rest
}) =>
  renderElement(
    as,
    {
      ...rest,
      className: cx(
        'fm-inline',
        `fm-inline--gap-${gap}`,
        `fm-inline--align-${align}`,
        justify !== 'start' && `fm-inline--justify-${justify}`,
        !wrap && 'fm-inline--nowrap',
        block && 'fm-inline--block',
        direction === 'column' && 'fm-inline--column',
        grow !== 'none' && `fm-inline--grow-${grow}`,
        className,
      ),
    },
    children,
  );

export interface FmTextProps extends FmBaseProps {
  size?: 'body' | 'caption' | 'fine' | 'tiny';
  tone?: 'default' | 'muted' | 'subtle' | 'accent' | 'positive' | 'negative';
  weight?: 'regular' | 'medium' | 'semibold' | 'bold';
  truncate?: boolean;
  /**
   * Quiet uppercase section-label treatment (the mini-heading above a stat
   * cluster / control). DS-owned so pages never restate `text-transform:
   * uppercase` + label sizing as a page-local class.
   */
  eyebrow?: boolean;
  /** Monospace treatment for inline identifiers / tool names / ids. */
  mono?: boolean;
}

export const FmText: React.FC<FmTextProps> = ({
  as = 'p',
  size = 'body',
  tone = 'default',
  weight = 'regular',
  truncate = false,
  eyebrow = false,
  mono = false,
  className,
  children,
  ...rest
}) =>
  renderElement(
    as,
    {
      ...rest,
      className: cx(
        'fm-text',
        `fm-text--${size}`,
        tone !== 'default' && `fm-text--${tone}`,
        weight !== 'regular' && `fm-text--${weight}`,
        truncate && 'fm-text--truncate',
        eyebrow && 'fm-text--eyebrow',
        mono && 'fm-text--mono',
        className,
      ),
    },
    children,
  );

export type FmHeadingLevel = 1 | 2 | 3 | 4 | 5;

export interface FmHeadingProps extends Omit<FmBaseProps, 'as'> {
  /** Semantic heading level -> h1-h5. Also controls size unless visualLevel is set. */
  level?: FmHeadingLevel;
  /** Optional DS type-scale size when the semantic level and visual size differ. */
  visualLevel?: FmHeadingLevel;
  tone?: 'default' | 'muted' | 'subtle' | 'accent' | 'positive' | 'negative';
}

/**
 * Heading primitive with the DS type scale — the replacement for AntD
 * `Typography.Title` (which FmText cannot cover, having no heading scale).
 * `level` maps to the semantic h1-h5 tag. `visualLevel` can opt into a
 * different `--fm-fs-h*` size while preserving the document outline.
 */
export const FmHeading: React.FC<FmHeadingProps> = ({
  level = 2,
  visualLevel,
  tone = 'default',
  className,
  children,
  ...rest
}) =>
  renderElement(
    `h${level}` as FmElement,
    {
      ...rest,
      className: cx(
        'fm-heading',
        `fm-heading--${visualLevel ?? level}`,
        tone !== 'default' && `fm-text--${tone}`,
        className,
      ),
    },
    children,
  );

export interface FmSeparatorProps extends Omit<React.HTMLAttributes<HTMLHRElement>, 'children'> {
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
  'data-testid'?: string;
}

export const FmSeparator: React.FC<FmSeparatorProps> = ({ className, ...rest }) => (
  <hr {...rest} className={cx('fm-separator', className)} />
);

export interface FmBoxProps extends FmBaseProps {
  /** Container padding as a semantic inset band. */
  inset?: FmInset;
  /**
   * Surface chrome. `panel` gives a neutral soft-bordered, rounded content
   * panel (the reusable replacement for a page-local
   * `border + border-radius` inline style) without the header/actions grammar
   * of FmCard. Default `none` keeps the box chrome-less.
   */
  surface?: 'none' | 'panel';
}

export const FmBox: React.FC<FmBoxProps> = ({
  as = 'div',
  inset = 'none',
  surface = 'none',
  className,
  children,
  ...rest
}) =>
  renderElement(
    as,
    {
      ...rest,
      className: cx(
        'fm-box',
        `fm-box--inset-${inset}`,
        surface !== 'none' && `fm-box--surface-${surface}`,
        className,
      ),
    },
    children,
  );

export interface FmCalloutProps extends Omit<FmBaseProps, 'as'> {
  /** Visual emphasis. `accent` is the neutral takeaway treatment;
   * `soft` adds a tinted background for the headline takeaway. */
  tone?: 'accent' | 'soft';
}

/**
 * Accent-bordered explanatory / takeaway block. Domain-neutral replacement for
 * the per-page "what this means" / takeaway callouts built with
 * page-local `border-left: … solid var(--fm-accent)` CSS. Keep the explanatory
 * sentence inside; never numbers without words.
 */
export const FmCallout: React.FC<FmCalloutProps> = ({
  tone = 'accent',
  className,
  children,
  ...rest
}) =>
  renderElement(
    'div',
    {
      ...rest,
      className: cx('fm-callout', tone === 'soft' && 'fm-callout--soft', className),
    },
    children,
  );
